/**
 * Pets product page parts, on pet product pages only: snippets/pets-head.liquid loads this file on the Pets product
 * template (template suffix pets, template name product), after pets-core.js and pets-profile.js. The faces
 * (<pets-faces>) stay in assets/pets-product.js, which every page loads for the quick view and the variant picker.
 *
 * <pets-ship-line> (blocks/pets-face-picker.liquid)
 *   The ship-from line of a pet product page. It holds one line per place a variant ships from
 *   ([data-pets-ship="us" | "cn"]) and a JSON map of variant id to place ([data-pets-ship-map]). On
 *   "lumi:variant:changed" for its section it shows the line for the chosen variant, and hides itself for
 *   a variant the map does not know, so nothing is claimed about it.
 *
 * <pets-pick-n> (blocks/pets-pick-n.liquid)
 *   Pick 2 / Pick 3: a "Just one" / "Pick 2 for $34.99" toggle. The many mode sets data-pets-mode="many" on
 *   <product-page>, where assets/pets.css hides the single buy box, and shows the slots. A face fills the
 *   chosen slot; each slot has its own size, starting on the size Lumi.petsCore.recommendSize gives for the
 *   pet profile. ?mode=two&a=&b= (and c=) opens the many mode with those faces. "Add both" sends one line per
 *   slot through Lumi.cart.add, with the product form's properties (the gift note), then opens the cart.
 *
 * <pets-size-finder> (blocks/pets-size-finder.liquid)
 *   The size finder: a button that opens chest and back (inches or cm) or weight (pounds) fields. It works the
 *   size out with Lumi.petsCore.recommendSize, says it in a status region and highlights the row of the size
 *   chart. It saves the measurements, never a size, with Lumi.pet.set, which also moves Pick 2 (it listens for
 *   "lumi:pets:changed"). It pins "best fit" on the matching radio of the theme's <variant-picker> in the same
 *   scope and checks it with a bubbling change; a size the shopper picks by hand (or a ?variant= link) stays
 *   until the measurements change. A size the picker marks sold out in the chosen style (data-unavailable) is
 *   skipped for the next one up, and "lumi:variant:changed" makes it look again when the style changes.
 *
 * <pets-gift-note> (blocks/pets-gift-note.liquid)
 *   "This is a gift" opens the note and enables it; unticked, the note is disabled, so neither the product form nor
 *   Pick 2 sends it. Every FormData made from the product form (Add to cart, the sticky bar, Pick 2) gets the note
 *   without its outer spaces, and none at all for a note of only spaces. While the box is ticked, the section's
 *   dynamic checkout button hides, so an express checkout never drops the note. It keeps the "0 / 160" counter.
 *   "Download gift card" loads assets/pets-canvas.js on its first click (once for the page; the card maker section
 *   loads it up front) and hands the note and the pet's name to Lumi.petsCanvas.giftCard, which draws and saves the
 *   card; if that fails, its status line says so.
 */
(function () {
  const Lumi = (window.Lumi = window.Lumi || {});

  // The theme's own product scripts (variant-picker.js, product.js) run after this file, from the page body. Work that
  // checks a radio waits for DOMContentLoaded, when all of them have run, so the picker hears the change; an element
  // connected later (a section re-rendered in the theme editor) starts at once.
  let pageReady = document.readyState === 'complete';
  if (!pageReady) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        pageReady = true;
      },
      { once: true }
    );
  }

  class PetsShipLine extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('script[data-pets-ship-map]');
      try {
        this.places = data ? JSON.parse(data.textContent) : {};
      } catch (e) {
        this.places = {};
      }
      this.onVariant = (e) => {
        const detail = e.detail;
        if (!detail || String(detail.sectionId) !== this.dataset.section) return;
        this.show(detail.variant ? this.places[String(detail.variant.id)] : '');
      };
      document.addEventListener('lumi:variant:changed', this.onVariant);
    }

    disconnectedCallback() {
      if (this.onVariant) document.removeEventListener('lumi:variant:changed', this.onVariant);
    }

    // Show the line for one place ("us" or "cn"); any other value hides the whole element.
    show(place) {
      let found = false;
      this.querySelectorAll('[data-pets-ship]').forEach((line) => {
        const match = line.dataset.petsShip === place;
        line.hidden = !match;
        if (match) found = true;
      });
      this.hidden = !found;
    }
  }

  if (!customElements.get('pets-ship-line')) customElements.define('pets-ship-line', PetsShipLine);

  // A face value as ?mode=two&a=&b= carries it: lower case, spaces to "-".
  const slug = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '-');
  const fill = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) => (Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match));
  const SLOT_PARAMS = ['a', 'b', 'c'];

  class PetsPickN extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      let data = null;
      try {
        data = JSON.parse(this.querySelector('script[data-pets-pick-data]').textContent);
      } catch (e) {
        data = null;
      }
      // Without its data the block stays hidden, and the normal buy box is all there is.
      if (!data || !Array.isArray(data.variants)) return;
      this._ready = true;
      this.data = data;
      this.page = this.closest('product-page');
      this.panel = this.querySelector('[data-pets-pick-panel]');
      this.hint = this.querySelector('[data-pets-pick-hint]');
      this.fullEl = this.querySelector('[data-pets-pick-full]');
      this.totalEl = this.querySelector('[data-pets-pick-total]');
      this.errorEl = this.querySelector('[data-pets-pick-error]');
      this.addBtn = this.querySelector('[data-pets-pick-add]');
      this.modes = [...this.querySelectorAll('[data-pets-pick-mode]')];
      this.faces = [...this.querySelectorAll('[data-pets-face]')];
      // A face looks sold out exactly when no variant with it is in stock, the same rule a click follows
      // (the snippet's own mark depends on the variant chosen in the picker).
      this.faces.forEach((face) => {
        const ok = this.faceAvailable(face.dataset.value);
        face.classList.toggle('pets-face--soldout', !ok);
        face.dataset.available = String(ok);
        const tag = face.querySelector('[data-soldout-tag]');
        if (tag) tag.hidden = ok;
        if (ok) face.removeAttribute('aria-disabled');
        else face.setAttribute('aria-disabled', 'true');
      });
      this.slots = [...this.querySelectorAll('[data-pets-slot]')].map((el) => {
        const faceEl = el.querySelector('[data-pets-slot-face]');
        return {
          el,
          faceEl,
          empty: faceEl.innerHTML,
          button: el.querySelector('[data-pets-slot-pick]'),
          name: el.querySelector('[data-pets-slot-name]'),
          select: el.querySelector('[data-pets-slot-size]'),
          face: '',
          size: '',
          touched: false,
          shown: '',
        };
      });
      this.mode = 'one';
      this.active = 0;
      this.readLink();

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onPet = () => {
        this.recommend();
        this.render();
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      document.addEventListener('lumi:pets:changed', this.onPet);
      // The first recommended sizes wait for the page's scripts (see pageReady).
      if (pageReady && Lumi.pet) this.recommend();
      else document.addEventListener('DOMContentLoaded', this.onPet, { once: true });
      this.hidden = false;
      this.render();
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._ready = false;
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('lumi:pets:changed', this.onPet);
      document.removeEventListener('DOMContentLoaded', this.onPet);
      // A block removed in the editor never leaves the page without its buy box.
      if (this.page) delete this.page.dataset.petsMode;
    }

    // ?mode=two&a=carrot&b=apple: the many mode, with each value that matches a face in stock in its slot.
    readLink() {
      const params = new URLSearchParams(window.location.search);
      if (!this.dataset.modeWord || params.get('mode') !== this.dataset.modeWord) return;
      this.mode = 'many';
      this.slots.forEach((slot, i) => {
        const wanted = slug(params.get(SLOT_PARAMS[i]));
        if (!wanted) return;
        const face = this.faces.find((f) => slug(f.dataset.value) === wanted);
        if (face && this.faceAvailable(face.dataset.value)) slot.face = face.dataset.value;
      });
      const empty = this.slots.findIndex((s) => !s.face);
      this.active = empty > -1 ? empty : 0;
    }

    variantFor(face, size) {
      const d = this.data;
      return d.variants.find((v) => v.options[d.face] === face && (d.size < 0 || v.options[d.size] === size)) || null;
    }

    faceAvailable(face) {
      const d = this.data;
      return d.variants.some((v) => v.available && v.options[d.face] === face);
    }

    sizeAvailable(face, size) {
      const variant = this.variantFor(face, size);
      return !!(variant && variant.available);
    }

    // The size the product's chart suggests for the pet profile's measurements, or ''.
    recommended() {
      if (!this.data.chart || !Lumi.petsCore || !Lumi.pet) return '';
      const fit = Lumi.petsCore.recommendSize(this.data.chart, Lumi.pet.get());
      return (fit && fit.size) || '';
    }

    // Slots the shopper has not sized by hand take the recommended size, when their face comes in it.
    recommend() {
      const size = this.recommended();
      if (!size) return;
      this.slots.forEach((slot) => {
        if (!slot.select || slot.touched) return;
        if (![...slot.select.options].some((o) => o.value === size)) return;
        if (slot.face && !this.sizeAvailable(slot.face, size)) return;
        slot.size = size;
      });
    }

    onClick(e) {
      const mode = e.target.closest('[data-pets-pick-mode]');
      if (mode) {
        this.setMode(mode.dataset.petsPickMode);
        return;
      }
      const slotButton = e.target.closest('[data-pets-slot-pick]');
      if (slotButton) {
        this.active = Math.max(0, this.slots.findIndex((s) => s.button === slotButton));
        this.render();
        return;
      }
      const face = e.target.closest('[data-pets-face]');
      if (face) {
        this.pick(face.dataset.value);
        return;
      }
      if (e.target.closest('[data-pets-pick-add]')) this.add();
    }

    onChange(e) {
      const slot = this.slots.find((s) => s.select && s.select === e.target);
      if (!slot) return;
      slot.size = slot.select.value;
      slot.touched = true;
      this.showError('');
      this.renderTotal();
    }

    setMode(mode) {
      this.mode = mode === 'many' ? 'many' : 'one';
      if (this.mode === 'one') this.forgetLink();
      this.showError('');
      this.render();
    }

    // "Just one" takes ?mode= and the slot faces out of the address, keeping every other param (variant too),
    // so a reload or a shared link opens the single buy box.
    forgetLink() {
      const url = new URL(window.location.href);
      const keys = ['mode', ...SLOT_PARAMS].filter((key) => url.searchParams.has(key));
      if (!keys.length) return;
      keys.forEach((key) => url.searchParams.delete(key));
      window.history.replaceState(window.history.state, '', url.toString());
    }

    // A face fills the chosen slot, then the next empty slot is the chosen one.
    pick(value) {
      if (!this.faceAvailable(value)) return;
      const slot = this.slots[this.active];
      slot.face = value;
      // The chosen slot moves on, so the face's pressed state does not say what happened: say it.
      Lumi.announce(fill(slot.el.dataset.nameFilled, { value }));
      // A size this face does not come in, or has sold out of, is cleared so the shopper picks again.
      if (slot.size && !this.sizeAvailable(value, slot.size)) {
        slot.size = '';
        slot.touched = false;
      }
      this.recommend();
      const n = this.slots.length;
      for (let step = 1; step < n; step += 1) {
        const i = (this.active + step) % n;
        if (!this.slots[i].face) {
          this.active = i;
          break;
        }
      }
      this.showError('');
      this.render();
    }

    render() {
      const many = this.mode === 'many';
      this.modes.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.petsPickMode === this.mode)));
      this.panel.hidden = !many;
      if (this.page) {
        if (many) this.page.dataset.petsMode = 'many';
        else delete this.page.dataset.petsMode;
      }
      this.slots.forEach((slot, i) => {
        slot.button.setAttribute('aria-pressed', String(i === this.active));
        if (slot.shown !== slot.face) {
          slot.shown = slot.face;
          const face = slot.face && this.faces.find((f) => f.dataset.value === slot.face);
          const img = face && face.querySelector('img');
          if (img) {
            const photo = img.cloneNode();
            photo.removeAttribute('loading');
            slot.faceEl.replaceChildren(photo);
          } else {
            slot.faceEl.innerHTML = slot.empty;
          }
          slot.faceEl.classList.toggle('is-filled', !!img);
          slot.name.textContent = slot.face ? fill(slot.el.dataset.nameFilled, { value: slot.face }) : slot.el.dataset.nameEmpty;
        }
        if (slot.select) {
          [...slot.select.options].forEach((o) => {
            if (o.value) o.disabled = !!slot.face && !this.sizeAvailable(slot.face, o.value);
          });
          slot.select.value = slot.size;
        }
      });
      const target = this.slots[this.active];
      if (this.hint) this.hint.textContent = target.el.dataset.hint || '';
      this.faces.forEach((f) => f.setAttribute('aria-pressed', String(!!target.face && f.dataset.value === target.face)));
      this.renderTotal();
    }

    // The real prices of the chosen variants (the first variant in stock for a slot not chosen yet), minus the
    // product's saving, which matches its automatic discount.
    renderTotal() {
      const d = this.data;
      const fallback = d.variants.find((v) => v.available) || d.variants[0];
      const full = this.slots.reduce((sum, slot) => {
        const chosen = slot.face && (d.size < 0 || slot.size) ? this.variantFor(slot.face, slot.size) : null;
        const variant = chosen || fallback;
        return sum + (variant ? Number(variant.price) || 0 : 0);
      }, 0);
      this.fullEl.textContent = Lumi.formatMoney(full);
      this.totalEl.textContent = Lumi.formatMoney(full - (Number(d.saving) || 0));
    }

    // Cleared first and set on the next frame, as Lumi.announce does, so the same message twice is heard twice.
    showError(message) {
      if (!this.errorEl) return;
      const turn = (this.errorTurn = (this.errorTurn || 0) + 1);
      this.errorEl.textContent = '';
      if (!message) return;
      window.requestAnimationFrame(() => {
        if (turn === this.errorTurn) this.errorEl.textContent = message;
      });
    }

    // The product form's line properties (the gift note), from every field tied to it with form="...".
    properties() {
      const form = document.getElementById(this.dataset.form);
      const out = {};
      if (!form) return out;
      for (const [key, value] of new FormData(form).entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && value.trim()) out[match[1]] = value.trim();
      }
      return out;
    }

    async add() {
      if (this.busy) return;
      // Every face first, then every size: name the first slot still waiting and go to it.
      const noFace = this.slots.findIndex((s) => !s.face);
      if (noFace > -1) {
        this.active = noFace;
        this.render();
        this.showError(this.slots[noFace].el.dataset.faceMissing);
        this.slots[noFace].button.focus();
        return;
      }
      const noSize = this.slots.findIndex((s) => s.select && (!s.size || !this.sizeAvailable(s.face, s.size)));
      if (noSize > -1) {
        this.showError(this.slots[noSize].el.dataset.sizeMissing);
        this.slots[noSize].select.focus();
        return;
      }

      const properties = this.properties();
      const items = this.slots.map((slot) => {
        const item = { id: this.variantFor(slot.face, slot.size).id, quantity: 1 };
        if (Object.keys(properties).length) item.properties = Object.assign({}, properties);
        return item;
      });

      this.busy = true;
      this.addBtn.setAttribute('aria-busy', 'true');
      this.showError('');
      try {
        await Lumi.cart.add(items);
        Lumi.emit('lumi:cart:open');
        Lumi.announce((Lumi.strings && Lumi.strings.productAdded) || '');
      } catch (err) {
        // The cart's own message (for example only so many left), in the block.
        this.showError((err && err.message) || (Lumi.strings && Lumi.strings.productError) || '');
      } finally {
        this.busy = false;
        this.addBtn.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('pets-pick-n')) customElements.define('pets-pick-n', PetsPickN);

  const CM_PER_IN = 2.54;
  // What a believable measurement is: 6 to 40 in around the chest, 4 to 30 in along the back (in cm), 1 to 200 lb.
  const LIMITS = { chest: [15.2, 101.6], back: [10.1, 76.2], weight: [1, 200] };
  const round1 = (n) => Math.round(n * 10) / 10;
  const upperFirst = (s) => s.charAt(0).toLocaleUpperCase() + s.slice(1);
  const measuresKey = (p) => [p.chestCm, p.backCm, p.weightLb].map((v) => v || '').join('|');
  const TYPING_MS = 800;

  class PetsSizeFinder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      let data = null;
      try {
        data = JSON.parse(this.querySelector('script[data-pets-fit-data]').textContent);
      } catch (e) {
        data = null;
      }
      const chart = data && Array.isArray(data.chart) ? data.chart.filter((row) => row && row.size) : [];
      // Without its chart the block stays hidden.
      if (!chart.length) return;
      this._ready = true;
      this.chart = chart;
      this.text = data.text || {};
      this.optionIndex = Number(data.option);
      this.start = this.start.bind(this);
      // The first work checks a size radio, so it waits for the page's scripts (see pageReady).
      if (pageReady && Lumi.pet && Lumi.petsCore) this.start();
      else document.addEventListener('DOMContentLoaded', this.start, { once: true });
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._ready = false;
      this.running = false;
      clearTimeout(this.timer);
      document.removeEventListener('DOMContentLoaded', this.start);
      document.removeEventListener('lumi:pets:changed', this.onPet);
      document.removeEventListener('lumi:variant:changed', this.onVariant);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('change', this.onChange);
      if (this.fieldset) this.fieldset.removeEventListener('change', this.onPicker);
      this.pin('');
    }

    start() {
      if (!this._ready || this.running || !Lumi.pet || !Lumi.petsCore) return;
      this.running = true;
      this.openBtn = this.querySelector('[data-pets-fit-open]');
      this.panel = this.querySelector('.pets-fit__panel');
      this.chestEl = this.querySelector('[data-pets-fit-chest]');
      this.backEl = this.querySelector('[data-pets-fit-back]');
      this.weightEl = this.querySelector('[data-pets-fit-weight]');
      this.resultEl = this.querySelector('[data-pets-fit-result]');
      this.rowsEl = this.querySelector('[data-pets-fit-rows]');
      this.problemEl = this.querySelector('[data-pets-fit-problem]');
      this.byBtns = [...this.querySelectorAll('[data-pets-fit-by]')];
      this.unitBtns = [...this.querySelectorAll('[data-pets-fit-unit]')];
      this.panes = [...this.querySelectorAll('[data-pets-fit-pane]')];
      this.unitLabels = [...this.querySelectorAll('label[data-in]')];
      try {
        this.numbers = new Intl.NumberFormat(document.documentElement.lang || undefined, { maximumFractionDigits: 1 });
      } catch (e) {
        this.numbers = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });
      }
      const scope = this.closest('[data-variant-scope]') || document;
      this.fieldset = this.optionIndex >= 0 ? scope.querySelector(`variant-picker [data-option-index="${this.optionIndex}"]`) : null;

      // Sizing by weight needs a weight limit on the chart's rows; without one every weight would be too big.
      this.hasWeight = this.chart.some((row) => row.available !== false && typeof row.max_lb === 'number');
      this.querySelectorAll('[data-pets-fit-weight-only]').forEach((el) => {
        el.hidden = !this.hasWeight;
      });

      this.by = 'measure';
      this.unit = 'in';
      this.problem = '';
      this.problemFields = [];
      // A ?variant= link already names a size; so does one the shopper chose.
      this.handPicked = new URLSearchParams(window.location.search).has('variant');

      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onPet = this.onPet.bind(this);
      this.onPicker = () => {
        if (!this.applying) this.handPicked = true;
      };
      // A style change changes which sizes are in stock, so best fit is looked at again (this section's picker only).
      this.onVariant = (e) => {
        const picker = e.detail && e.detail.picker;
        if (this.applying || !this.fieldset || !picker || !picker.contains(this.fieldset)) return;
        this.render(Lumi.pet.get(), true);
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onChange);
      document.addEventListener('lumi:pets:changed', this.onPet);
      document.addEventListener('lumi:variant:changed', this.onVariant);
      if (this.fieldset) this.fieldset.addEventListener('change', this.onPicker);

      const profile = Lumi.pet.get();
      this.key = measuresKey(profile);
      this.syncBy(profile);
      this.renderUnits();
      this.fillFields(profile);
      this.render(profile, true);
      this.hidden = false;
    }

    // The length as the shopper reads it, in the chosen unit.
    length(cm) {
      const inches = this.unit === 'in';
      return `${this.numbers.format(inches ? cm / CM_PER_IN : cm)} ${inches ? this.text.unitIn : this.text.unitCm}`;
    }

    fieldValue(cm) {
      return String(round1(this.unit === 'in' ? cm / CM_PER_IN : cm));
    }

    onClick(e) {
      if (e.target.closest('[data-pets-fit-open]')) {
        this.toggle();
        return;
      }
      const by = e.target.closest('[data-pets-fit-by]');
      if (by) {
        this.by = by.dataset.petsFitBy;
        this.clearProblem();
        this.renderBy();
        this.render(Lumi.pet.get(), false);
        return;
      }
      const unit = e.target.closest('[data-pets-fit-unit]');
      if (unit) {
        this.unit = unit.dataset.petsFitUnit;
        this.clearProblem();
        this.renderUnits();
        const profile = Lumi.pet.get();
        this.fillFields(profile);
        this.render(profile, false);
      }
    }

    // Typing waits for a pause (so "1" on the way to "15" is not judged); leaving the field saves at once.
    onInput(e) {
      if (![this.chestEl, this.backEl, this.weightEl].includes(e.target)) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.commit(), TYPING_MS);
    }

    onChange(e) {
      if (![this.chestEl, this.backEl, this.weightEl].includes(e.target)) return;
      clearTimeout(this.timer);
      this.commit();
    }

    toggle() {
      const open = this.panel.hidden;
      this.panel.hidden = !open;
      this.openBtn.setAttribute('aria-expanded', String(open));
      if (open) (this.by === 'weight' ? this.weightEl : this.chestEl).focus();
    }

    // What the fields ask the profile to change, and the first thing wrong with them. "values" is a patch for
    // Lumi.pet.set (an undefined value takes a saved one away), or null when nothing believable was typed: a half-typed
    // or mistyped number saves nothing, so the saved values (and Pick 2's sizes) stay.
    readMeasure() {
      const cm = (el) => {
        const v = parseFloat(el.value);
        return Number.isFinite(v) ? round1(this.unit === 'in' ? v * CM_PER_IN : v) : null;
      };
      const chest = cm(this.chestEl);
      const back = cm(this.backEl);
      // An emptied chest takes the chest and back away, and leaves a weight alone.
      if (chest === null) return { values: { chestCm: undefined, backCm: undefined }, problem: '', fields: [] };
      if (chest < LIMITS.chest[0] || chest > LIMITS.chest[1]) return { values: null, problem: this.text.offChest, fields: [this.chestEl] };
      const out = { values: { chestCm: chest, weightLb: undefined }, problem: '', fields: [] };
      if (back === null) out.values.backCm = undefined;
      else if (back < LIMITS.back[0] || back > LIMITS.back[1]) Object.assign(out, { problem: this.text.offBack, fields: [this.backEl] });
      else out.values.backCm = back;
      return out;
    }

    readWeight() {
      const lb = parseFloat(this.weightEl.value);
      if (!Number.isFinite(lb)) return { values: { weightLb: undefined }, problem: '', fields: [] };
      if (lb < LIMITS.weight[0] || lb > LIMITS.weight[1]) return { values: null, problem: this.text.offWeight, fields: [this.weightEl] };
      return { values: { weightLb: round1(lb), chestCm: undefined, backCm: undefined }, problem: '', fields: [] };
    }

    // Saves the measurements of the way being used; saving one clears the other, as the Make it theirs size tab does.
    // The profile event (onPet) then does the rest, here and in Pick 2.
    commit() {
      const weight = this.by === 'weight';
      const next = weight ? this.readWeight() : this.readMeasure();
      this.problem = next.problem;
      this.problemFields = next.fields;
      if (!next.values) {
        this.render(Lumi.pet.get(), false);
        return;
      }
      // The other way's fields empty only once a number of this way is saved.
      if (weight && next.values.weightLb !== undefined) {
        this.chestEl.value = '';
        this.backEl.value = '';
      } else if (!weight && next.values.chestCm !== undefined) {
        this.weightEl.value = '';
      }
      this.saving = true;
      try {
        Lumi.pet.set(next.values);
      } finally {
        this.saving = false;
      }
    }

    // A message belongs to what was typed: any other change takes it away.
    clearProblem() {
      this.problem = '';
      this.problemFields = [];
    }

    onPet() {
      const profile = Lumi.pet.get();
      const key = measuresKey(profile);
      const moved = key !== this.key;
      this.key = key;
      // New measurements may move the size again, even one chosen by hand.
      if (moved) this.handPicked = false;
      if (!this.saving) {
        this.clearProblem();
        this.syncBy(profile);
        this.fillFields(profile);
      }
      this.render(profile, moved);
    }

    // The way of sizing the profile was saved with.
    syncBy(profile) {
      if (profile.weightLb && this.hasWeight) this.by = 'weight';
      else if (profile.chestCm) this.by = 'measure';
      this.renderBy();
    }

    renderBy() {
      this.byBtns.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.petsFitBy === this.by)));
      this.panes.forEach((p) => {
        p.hidden = p.dataset.petsFitPane !== this.by;
      });
    }

    renderUnits() {
      this.unitBtns.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.petsFitUnit === this.unit)));
      this.unitLabels.forEach((label) => {
        label.textContent = label.dataset[this.unit];
      });
    }

    fillFields(profile) {
      this.chestEl.value = profile.chestCm ? this.fieldValue(profile.chestCm) : '';
      this.backEl.value = profile.backCm ? this.fieldValue(profile.backCm) : '';
      this.weightEl.value = profile.weightLb ? String(round1(profile.weightLb)) : '';
    }

    // The pet's size on the whole chart (wanted), and in stock in the chosen style (fit): a size the picker marks
    // data-unavailable is skipped for the next one up. Best fit goes on the one in stock.
    render(profile, select) {
      const core = Lumi.petsCore;
      // A weight means nothing on a chart with no weight limits.
      const measures = this.hasWeight ? profile : Object.assign({}, profile, { weightLb: undefined });
      const wanted = core.recommendSize(this.chart, measures);
      const fit = core.recommendSize(this.stockedChart(), measures);
      const size = (fit && fit.size) || '';
      this.pin(size);
      this.renderRows(size);
      this.renderResult(profile, wanted, fit);
      this.renderProblem();
      if (select && size && !this.handPicked) this.select(size);
    }

    stockedChart() {
      return this.chart.map((row) => {
        const radio = this.radioFor(row.size);
        return radio && radio.hasAttribute('data-unavailable') ? Object.assign({}, row, { available: false }) : row;
      });
    }

    radioFor(size) {
      if (!this.fieldset) return null;
      return [...this.fieldset.querySelectorAll('input[type="radio"]')].find((r) => r.value === size) || null;
    }

    // "best fit" on the label of the recommended size; it becomes part of the radio's name.
    pin(size) {
      if (this.pinned) this.pinned.remove();
      this.pinned = null;
      const radio = size && this.radioFor(size);
      const label = radio && radio.closest('label');
      if (!label) return;
      const pin = document.createElement('span');
      pin.className = 'pets-bestfit';
      pin.textContent = this.text.bestFit;
      label.append(pin);
      this.pinned = pin;
    }

    // Checks the size's radio the way pets-faces does, so the price, photo and Add to cart follow.
    select(size) {
      const radio = this.radioFor(size);
      if (!radio || radio.checked) return;
      this.applying = true;
      try {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      } finally {
        this.applying = false;
      }
    }

    renderRows(size) {
      const rows = this.chart.map((row) => {
        const yours = !!size && row.size === size;
        const tr = document.createElement('tr');
        tr.classList.toggle('is-yours', yours);
        const head = document.createElement('th');
        head.scope = 'row';
        const name = document.createElement('span');
        name.className = 'pets-fit__size';
        name.textContent = row.size;
        head.append(name);
        if (yours) {
          const mark = document.createElement('span');
          mark.className = 'pets-fit__mark';
          mark.textContent = this.text.bestFit;
          head.append(mark);
        }
        if (row.fits) {
          const fits = document.createElement('span');
          fits.className = 'pets-fit__fits';
          fits.textContent = row.fits;
          head.append(fits);
        }
        tr.append(head);
        [row.chest_cm, row.back_cm].forEach((cm) => {
          const td = document.createElement('td');
          td.textContent = typeof cm === 'number' ? this.length(cm) : '';
          tr.append(td);
        });
        return tr;
      });
      this.rowsEl.replaceChildren(...rows);
    }

    // The note on how a size fits: weight is a rough guide; a chest says snug or roomy, and a long back.
    fitNote(fit) {
      const t = this.text;
      if (fit.basis === 'weight') return t.weightNote;
      let note = fit.snug && fit.next ? fill(t.snug, { next: fit.next }) : t.roomy;
      if (fit.shortBody) note += ` ${t.shortBody}`;
      return note;
    }

    // Says the result in the status region: the size (with a note on the fit), a best guess from weight, a size
    // sold out in this style, a size beyond the chart, or what is missing. Identical words are not set twice, so
    // they are not read twice.
    renderResult(profile, wanted, fit) {
      const t = this.text;
      const core = Lumi.petsCore;
      const named = (template, values) => core.fillTemplate(fill(template, values), profile);
      const usable = this.chart.filter((row) => row.available !== false);
      const last = usable[usable.length - 1] || this.chart[this.chart.length - 1];
      let lead = '';
      let note = '';
      if (wanted && wanted.size) {
        if (!fit || !fit.size) {
          // Sold out in this style, with nothing bigger in stock: not "bigger than our biggest".
          lead = fill(t.soldOutLast, { size: wanted.size });
          if (wanted.basis === 'weight') note = t.weightNote;
        } else if (fit.size !== wanted.size) {
          lead = fill(t.soldOut, { size: wanted.size, next: fit.size });
          note = this.fitNote(fit);
        } else {
          lead = named(fit.basis === 'weight' ? t.guess : t.result, { size: fit.size });
          note = this.fitNote(fit);
        }
      } else if (wanted && wanted.tooBig) {
        lead = fill(t.tooBig, { size: last.size });
        if (wanted.basis === 'weight') note = t.weightNote;
        else if (typeof last.chest_cm === 'number' && typeof core.EASE_CM === 'number') {
          // The largest chest that fits is the biggest garment's chest less the room it needs.
          note = fill(t.tooBigNote, { size: last.size, chest: this.length(last.chest_cm - core.EASE_CM) });
        }
      }
      const empty = !lead;
      if (empty) note = this.by === 'weight' ? t.emptyWeight : t.emptyMeasure;
      lead = upperFirst(lead);

      const said = `${lead}\n${note}`;
      if (said === this.said) return;
      this.said = said;
      this.resultEl.classList.toggle('is-empty', empty);
      const parts = [];
      if (lead) {
        const strong = document.createElement('strong');
        strong.textContent = lead;
        parts.push(strong);
      }
      if (note) {
        const span = document.createElement('span');
        span.textContent = note;
        parts.push(span);
      }
      this.resultEl.replaceChildren(...parts);
    }

    // The message about a number that cannot be right, in its own status region, tied to the field it is about.
    renderProblem() {
      const message = this.problem || '';
      if (message !== this.problemShown) {
        this.problemShown = message;
        this.problemEl.textContent = message;
      }
      [this.chestEl, this.backEl, this.weightEl].forEach((el) => {
        if (message && this.problemFields.includes(el)) {
          el.setAttribute('aria-invalid', 'true');
          el.setAttribute('aria-describedby', this.problemEl.id);
        } else {
          el.removeAttribute('aria-invalid');
          el.removeAttribute('aria-describedby');
        }
      });
    }
  }

  if (!customElements.get('pets-size-finder')) customElements.define('pets-size-finder', PetsSizeFinder);

  // assets/pets-canvas.js, loaded once for the page on the first "Download gift card" click and shared by every later
  // click. The card maker section loads it up front, so there it is already here. A load that fails can be tried again.
  let canvasLoad = null;
  function loadCanvas(src) {
    if (Lumi.petsCanvas) return Promise.resolve(Lumi.petsCanvas);
    if (!canvasLoad) {
      canvasLoad = new Promise((resolve, reject) => {
        if (!src) {
          reject(new Error('No pets-canvas.js address'));
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => (Lumi.petsCanvas ? resolve(Lumi.petsCanvas) : reject(new Error('pets-canvas.js did not set up')));
        script.onerror = () => {
          script.remove();
          reject(new Error('pets-canvas.js did not load'));
        };
        document.head.append(script);
      }).catch((err) => {
        canvasLoad = null;
        throw err;
      });
    }
    return canvasLoad;
  }

  class PetsGiftNote extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.box = this.querySelector('[data-pets-gift-on]');
      this.body = this.querySelector('[data-pets-gift-body]');
      this.note = this.querySelector('[data-pets-gift-note]');
      this.count = this.querySelector('[data-pets-gift-count]');
      this.download = this.querySelector('[data-pets-gift-download]');
      this.statusEl = this.querySelector('[data-pets-gift-status]');
      if (!this.box || !this.body || !this.note) return;
      this._ready = true;
      this.form = document.getElementById(this.note.getAttribute('form') || '');
      this.sync = this.sync.bind(this);
      this.renderCount = this.renderCount.bind(this);
      this.giftCard = this.giftCard.bind(this);
      this.onFormData = this.onFormData.bind(this);
      this.box.addEventListener('change', this.sync);
      this.note.addEventListener('input', this.renderCount);
      if (this.download) this.download.addEventListener('click', this.giftCard);
      if (this.form) this.form.addEventListener('formdata', this.onFormData);
      this.sync();
      this.renderCount();
      this.hidden = false;
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._ready = false;
      this.box.removeEventListener('change', this.sync);
      this.note.removeEventListener('input', this.renderCount);
      if (this.download) this.download.removeEventListener('click', this.giftCard);
      if (this.form) this.form.removeEventListener('formdata', this.onFormData);
      // A block removed in the editor never leaves the express checkout hidden.
      this.showExpress(true);
    }

    // The note counts only while the box is ticked: a disabled field is left out of the form's data. While it is
    // ticked, the express checkout hides, since it could check out without the note.
    sync() {
      const on = this.box.checked;
      this.body.hidden = !on;
      this.note.disabled = !on;
      this.showExpress(!on);
    }

    // This section's dynamic checkout button (Buy it now, More payment options), in the product form. Only what this
    // element hid is shown again.
    showExpress(show) {
      if (!this.form) return;
      const wraps = [...this.form.querySelectorAll('.pbuy__dynamic')];
      (wraps.length ? wraps : [...this.form.querySelectorAll('.shopify-payment-button')]).forEach((el) => {
        if (!show && !el.hidden) {
          el.hidden = true;
          el.dataset.petsGiftHid = '';
        } else if (show && el.hasAttribute('data-pets-gift-hid')) {
          el.hidden = false;
          delete el.dataset.petsGiftHid;
        }
      });
    }

    // Every FormData made from the product form (product.js for Add to cart and the sticky bar, Pick 2) takes the note
    // without its outer spaces, and leaves out a note of only spaces.
    onFormData(e) {
      if (this.note.disabled || !this.note.name) return;
      const note = this.note.value.trim();
      e.formData.delete(this.note.name);
      if (note) e.formData.append(this.note.name, note);
    }

    renderCount() {
      if (!this.count) return;
      this.count.textContent = fill(this.dataset.countTemplate, { count: this.note.value.length, max: this.note.maxLength });
    }

    async giftCard() {
      if (this.busy) return;
      this.busy = true;
      this.download.setAttribute('aria-busy', 'true');
      this.say('');
      try {
        const canvas = await loadCanvas(this.dataset.canvasSrc);
        const profile = Lumi.pet ? Lumi.pet.get() : {};
        await canvas.giftCard({ note: this.note.value, name: profile.name || '', productTitle: this.dataset.productTitle || '' });
      } catch (err) {
        // The canvas script did not load, or the card did not draw or save: said under the button, as the card maker does.
        this.say((Lumi.strings && Lumi.strings.petsCardSaveError) || '');
      } finally {
        this.busy = false;
        this.download.removeAttribute('aria-busy');
      }
    }

    // Cleared first and set on the next frame, so the same message twice is heard twice.
    say(message) {
      if (!this.statusEl) return;
      const turn = (this.sayTurn = (this.sayTurn || 0) + 1);
      this.statusEl.textContent = '';
      if (!message) return;
      window.requestAnimationFrame(() => {
        if (turn === this.sayTurn) this.statusEl.textContent = message;
      });
    }
  }

  if (!customElements.get('pets-gift-note')) customElements.define('pets-gift-note', PetsGiftNote);
})();
